import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import { SETUP_STEPS, type SetupProgress, type SetupStepId, type SetupStepStatus } from "./setupProgress";

/** What the guide shows: the welcome, one step, or the end screen. */
export type SetupView = "welcome" | SetupStepId | "done";

const MARKS: Readonly<Record<SetupStepStatus, string>> = {
  done: "✓",
  current: "●",
  to_do: "○",
  waiting_for_administrator: "…",
  not_applicable: "–",
};

/** The stepper: one status mark per step; a step can be looked at again at any time. */
export default function SetupStepList({ progress, shown, onChoose }: { progress: SetupProgress; shown: SetupView; onChoose: (view: SetupView) => void }) {
  const { t } = useTranslation();
  const steps = SETUP_STEPS.filter((step) => progress.steps[step] !== "not_applicable");
  // The end screen says the PC is ready, so it is there only once it is.
  const views: readonly SetupView[] = progress.complete ? ["welcome", ...steps, "done"] : ["welcome", ...steps];
  return (
    <nav aria-label={t("connectors.setup.stepsLabel")}>
      <ol className="app-card space-y-1 p-3 text-sm">
        {views.map((view) => {
          const status = view === "welcome" || view === "done" ? "done" : progress.steps[view];
          return (
            <li key={view}>
              <button
                type="button"
                aria-current={view === shown ? "step" : undefined}
                className={classNames("app-action-link-muted w-full rounded px-2 py-1 text-left", view === shown && "app-nav-item-active font-semibold")}
                onClick={() => { onChoose(view); }}
              >
                <span aria-hidden="true" className="mr-2 inline-block w-3">{MARKS[status]}</span>
                {t(`connectors.setup.steps.${view}`)}
                <span className="sr-only"> · {t(`connectors.setup.status.${status}`)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
